import {
  withDuration as withScheduleDuration,
  withEndDate as withScheduleEndDate,
  withStartDate as withScheduleStartDate
} from "../../../DocumentTemplates/shared/duration-date-calculator.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"

/**
 * A row the API has never seen carries this prefix instead of a UUID; the PATCH
 * strips the whole `id`, which is what tells the serializer to create it.
 */
const DRAFT_MILESTONE_PREFIX = "_"

const isDraftMilestone = (phase?: LeadFormDataPhase | null) =>
  !!phase?.id?.startsWith(DRAFT_MILESTONE_PREFIX)

const sortMilestones = (phases?: LeadFormDataPhase[] | null) =>
  [...(phases ?? [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))

const createMilestone = (
  order: number,
  projectTypeId?: string | null
): LeadFormDataPhase => ({
  id: `${DRAFT_MILESTONE_PREFIX}${crypto.randomUUID()}`,
  name: "",
  order,
  budget: { budget: 0 },
  project_type: projectTypeId ?? "",
  checklist_data: []
})

const milestonePrice = (phase?: LeadFormDataPhase | null) => {
  const price = Number(phase?.budget?.budget ?? 0)
  return Number.isFinite(price) ? price : 0
}

const withMilestonePrice = (
  phase: LeadFormDataPhase,
  price?: number | null
): LeadFormDataPhase => ({
  ...phase,
  budget: { ...phase?.budget, budget: price ?? 0 }
})

const milestoneDuration = (phase?: LeadFormDataPhase | null) => {
  const duration = Number(phase?.duration_days ?? 0)
  return Number.isFinite(duration) ? duration : 0
}

/**
 * Last edit wins, and one edit derives at most one other field: a date fills
 * the duration when the opposite date is there, and a duration fills whichever
 * date is missing — the end when a start exists, else the start from the end.
 * An end before its start yields no duration rather than a negative one, and
 * clearing any field touches nothing else.
 *
 * The actual day/unit maths now lives in the shared, unit-aware
 * `duration-date-calculator.ts` engine (the same one the Proposal/Contract
 * editor uses) — a Lead milestone has no `duration_type` field of its own
 * today, so every call here fixes the unit at "days" and only ever reads
 * back `.duration`/`.startDate`/`.endDate`, never the shared engine's
 * `durationUnit`. Days-only is a strict subset of the shared engine's
 * behaviour, so this migration is invisible to existing Lead behaviour.
 */
const withMilestoneStartDate = (
  phase: LeadFormDataPhase,
  startDate?: string | null
): LeadFormDataPhase => {
  const derived = withScheduleStartDate(
    { startDate: phase.start_date, endDate: phase.end_date },
    startDate
  )
  const next: LeadFormDataPhase = {
    ...phase,
    start_date: derived.startDate ?? null
  }
  return derived.duration != null
    ? { ...next, duration_days: derived.duration }
    : next
}

const withMilestoneEndDate = (
  phase: LeadFormDataPhase,
  endDate?: string | null
): LeadFormDataPhase => {
  const derived = withScheduleEndDate(
    { startDate: phase.start_date, endDate: phase.end_date },
    endDate
  )
  const next: LeadFormDataPhase = {
    ...phase,
    end_date: derived.endDate ?? null
  }
  return derived.duration != null
    ? { ...next, duration_days: derived.duration }
    : next
}

const withMilestoneDuration = (
  phase: LeadFormDataPhase,
  duration?: number | null
): LeadFormDataPhase => {
  const derived = withScheduleDuration(
    { startDate: phase.start_date, endDate: phase.end_date },
    duration,
    "days"
  )
  const next: LeadFormDataPhase = { ...phase, duration_days: duration ?? null }

  if ((derived.endDate ?? null) !== (phase.end_date ?? null)) {
    return { ...next, end_date: derived.endDate ?? null }
  }
  if ((derived.startDate ?? null) !== (phase.start_date ?? null)) {
    return { ...next, start_date: derived.startDate ?? null }
  }
  return next
}

const milestoneReceivable = (phase?: LeadFormDataPhase | null) => {
  const value = Number(phase?.planned_receivable ?? 0)
  return Number.isFinite(value) ? value : 0
}

const milestonePayable = (phase?: LeadFormDataPhase | null) => {
  const value = Number(phase?.planned_payable ?? 0)
  return Number.isFinite(value) ? value : 0
}

const withMilestoneReceivable = (
  phase: LeadFormDataPhase,
  value?: number | null
): LeadFormDataPhase => ({
  ...phase,
  planned_receivable: value ?? 0
})

const withMilestonePayable = (
  phase: LeadFormDataPhase,
  value?: number | null
): LeadFormDataPhase => ({
  ...phase,
  planned_payable: value ?? 0
})

const milestonesReceivableTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestoneReceivable(phase), 0)

const milestonesPayableTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestonePayable(phase), 0)

const milestonePayableExceedsReceivable = (phase?: LeadFormDataPhase | null) =>
  milestonePayable(phase) > milestoneReceivable(phase)

const isMilestoneNamed = (phase?: LeadFormDataPhase | null) =>
  !!phase?.name?.trim()

const milestonesAreNamed = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).every(isMilestoneNamed)

const milestonesTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestonePrice(phase), 0)

const milestonesDurationTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestoneDuration(phase), 0)

const moveMilestone = (
  phases: LeadFormDataPhase[],
  from: number,
  to: number
): LeadFormDataPhase[] => {
  if (from === to || from < 0 || to < 0 || from >= phases.length) return phases
  if (to >= phases.length) return phases

  const moved = [...phases]
  const [row] = moved.splice(from, 1)
  moved.splice(to, 0, row)

  return moved.map((phase, index) => ({ ...phase, order: index }))
}

/**
 * `order` is re-derived from the row's place in the table, so a reorder that
 * never touched a row still persists its new position.
 */
const GENERIC_REFUSAL = "Validation error"

const firstText = (value: unknown): string | null => {
  if (typeof value === "string") return value || null
  if (Array.isArray(value))
    return value.find((item) => typeof item === "string") ?? null
  return null
}

/**
 * The reason the server gave for refusing a milestones PATCH, or `null` when it
 * gave none worth showing. A refusal raised for one milestone of the lead
 * payload arrives nested under its index in `details.project_phases`, beside
 * the handler's generic "Validation error", so it is named after that row.
 */
const milestonesSaveRefusal = (
  payload: unknown,
  sent: LeadFormDataPhase[],
  nameOf: (milestone: LeadFormDataPhase) => string
): string | null => {
  const details =
    (payload as { details?: Record<string, unknown> } | null)?.details ?? {}
  const nested = details.project_phases
  if (nested && typeof nested === "object") {
    const rows: [string, unknown][] = Array.isArray(nested)
      ? nested.map((errors, index) => [String(index), errors])
      : Object.entries(nested)
    for (const [index, errors] of rows) {
      if (!errors || typeof errors !== "object") continue
      for (const value of Object.values(errors)) {
        const text = firstText(value)
        if (!text) continue
        const milestone = sent[Number(index)]
        return milestone ? `${nameOf(milestone)}: ${text}` : text
      }
    }
  }
  const message = firstText(details.message)
  return message && message !== GENERIC_REFUSAL ? message : null
}

const serializeMilestonesForPatch = (
  phases?: LeadFormDataPhase[] | null
): LeadFormDataPhase[] =>
  (phases ?? []).map(({ id, project_type, ...phase }, index) => ({
    ...phase,
    order: index,
    ...(project_type ? { project_type } : {}),
    ...(id?.startsWith(DRAFT_MILESTONE_PREFIX) ? {} : { id })
  })) as LeadFormDataPhase[]

export {
  DRAFT_MILESTONE_PREFIX,
  isDraftMilestone,
  sortMilestones,
  createMilestone,
  milestonePrice,
  withMilestonePrice,
  milestoneDuration,
  withMilestoneStartDate,
  withMilestoneEndDate,
  withMilestoneDuration,
  milestoneReceivable,
  milestonePayable,
  withMilestoneReceivable,
  withMilestonePayable,
  milestonesReceivableTotal,
  milestonesPayableTotal,
  milestonePayableExceedsReceivable,
  isMilestoneNamed,
  milestonesAreNamed,
  milestonesTotal,
  milestonesDurationTotal,
  moveMilestone,
  milestonesSaveRefusal,
  serializeMilestonesForPatch
}
